import { useContext, useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  SimpleGrid,
  HStack,
  Divider,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import CustomInput from "components/common/customInput/CustomInput";
import ShadowBox from "components/common/ShadowBox/ShadowBox";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";

type EmergingWtc = {
  _id?: string;
  glCode: string;
  description: string;
  tax?: number;
  companyCode?: string;
  createdBy?: string;
  createdAt?: Date;
};

const initialForm: EmergingWtc = {
  glCode: "",
  description: "",
  tax: undefined,
  companyCode: "",
  createdBy: "",
};

export default function EmergingWtcScreen(): JSX.Element {
  const { toastNotify } = useContext(AppContext);

  const [form, setForm] = useState<EmergingWtc>(initialForm);
  const [list, setList] = useState<EmergingWtc[]>([]);

  const [page, setPage] = useState(1);
  const [limit] = useState(500);

  const [searchCompanyCode, setSearchCompanyCode] = useState("");

  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const [deleteId, setDeleteId] = useState<string | undefined>(undefined);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isAlertOpen, onOpen: onAlertOpen, onClose: onAlertClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const handleChange = (name: keyof EmergingWtc, value: string) => {
    setForm((prev) => ({ ...prev, [name]: name === "tax" ? (value === "" ? undefined : Number(value)) : value }));
  };

  const fetchEmergingWtc = async () => {
    try {
      const res: any = await appRequest("emergingWtc", "getAll", {
        page,
        limit,
        companyCode: searchCompanyCode || undefined,
      });

      if (res?.status === "success") {
        setList(res?.data?.data || []);
      }
    } catch (err: any) {
      toastNotify({
        title: "Fetch failed",
        message: err?.message || "Something went wrong",
        type: "error",
      });
    }
  };

  useEffect(() => {
    fetchEmergingWtc();
  }, [page, searchCompanyCode]);

  const handleAdd = async () => {
    if (!form.glCode || !form.description) {
      toastNotify({
        title: "Validation error",
        message: "GL Code and Description are required",
        type: "error",
      });
      return;
    }

    try {
      setLoading(true);

      if (editingId) {
        await appRequest("emergingWtc", "update", { id: editingId, ...form });
      } else {
        await appRequest("emergingWtc", "create", form);
      }

      toastNotify({
        title: "Success",
        message: editingId ? "Updated successfully" : "Saved successfully",
        type: "success",
      });

      setForm(initialForm);
      setEditingId(undefined);
      fetchEmergingWtc();
      onClose();
    } catch (err: any) {
      toastNotify({
        title: "Operation failed",
        message: err?.message || "Something went wrong",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id?: string) => {
    if (!id) return;
    setDeleteId(id);
    onAlertOpen();
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    
    try {
      setLoading(true);
      await appRequest("emergingWtc", "delete", { id: deleteId });

      toastNotify({
        title: "Deleted",
        message: "Record removed successfully",
        type: "success",
      });

      fetchEmergingWtc();
    } catch (err: any) {
      toastNotify({
        title: "Delete failed",
        message: err?.message || "Something went wrong",
        type: "error",
      });
    } finally {
      setLoading(false);
      setDeleteId(undefined);
      onAlertClose();
    }
  };

  return (
    <ShadowBox>
      <Flex mb={4} align="center" justify="space-between">
        <Box fontWeight="bold" fontSize="lg">
          Emerging WTC Master
        </Box>

        <Button
          colorScheme="teal"
          onClick={() => {
            setForm(initialForm);
            setEditingId(undefined);
            onOpen();
          }}
        >
          Add
        </Button>
      </Flex>

      <HStack spacing={4} mb={4}>
        <CustomInput
          name="searchCompanyCode"
          label="Company Code"
          value={searchCompanyCode}
          onChange={(e: any) => setSearchCompanyCode(e.target.value)}
        />
      </HStack>

      <Box mt={3} maxH="60vh" overflowY="auto">
        <Table size="sm" variant="striped">
          <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
            <Tr>
              <Th>GL Code</Th>
              <Th>Description</Th>
              <Th>Tax</Th>
              <Th>Company Code</Th>
              <Th>Created By</Th>
              <Th>Created At</Th>
              <Th>Action</Th>
            </Tr>
          </Thead>

          <Tbody>
            {list.map((item) => (
              <Tr key={item._id}>
                <Td>{item.glCode}</Td>
                <Td>{item.description}</Td>
                <Td>{item.tax}</Td>
                <Td>{item.companyCode}</Td>
                <Td>{item.createdBy}</Td>
                <Td>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : ""}</Td>
                <Td>
                  <HStack>
                    <Button
                      size="xs"
                      variant="outline"
                      colorScheme="blue"
                      onClick={() => {
                        setForm(item);
                        setEditingId(item._id);
                        onOpen();
                      }}
                    >
                      Edit
                    </Button>

                    <Button
                      size="xs"
                      variant="outline"
                      colorScheme="red"
                      onClick={() => handleDelete(item._id)}
                    >
                      Delete
                    </Button>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* MODAL */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(6px)" />
        <ModalContent borderRadius="2xl" boxShadow="2xl">
          <ModalHeader fontSize="lg" fontWeight="600">
            {editingId ? "Edit Emerging WTC" : "Add Emerging WTC"}
          </ModalHeader>

          <Divider />

          <ModalBody py={5}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <CustomInput name="glCode" label="GL Code" value={form.glCode} onChange={(e: any) => handleChange("glCode", e.target.value)} />
              <CustomInput name="description" label="Description" value={form.description} onChange={(e: any) => handleChange("description", e.target.value)} />
              <CustomInput name="tax" label="Tax" type="number" value={form.tax === undefined ? "" : form.tax} onChange={(e: any) => handleChange("tax", e.target.value)} />
              <CustomInput name="companyCode" label="Company Code" value={form.companyCode || ""} onChange={(e: any) => handleChange("companyCode", e.target.value)} />
            </SimpleGrid>
          </ModalBody>

          <Divider />

          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="teal" onClick={handleAdd} isLoading={loading}>
              {editingId ? "Update" : "Save"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ALERT DIALOG */}
      <AlertDialog
        isOpen={isAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onAlertClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="2xl" boxShadow="2xl">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Emerging WTC
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this record? You can't undo this action.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onAlertClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDelete} ml={3} isLoading={loading}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </ShadowBox>
  );
}
